import { useState, useEffect, useCallback, useRef } from 'react'
import { useSocket } from '../contexts/SocketContext'
import { apiGet } from '../utils/api'

/**
 * Lightweight hook that tracks the total number of active (queued + open)
 * conversations for the sidebar badge. Independent of queue view filters.
 */
export const useInboxCount = (): number => {
  const [count, setCount] = useState(0)
  const { socket } = useSocket()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchCount = useCallback(async () => {
    try {
      // /queue returns { queue, queue_depth }; older/other shapes use { chats }
      // or a bare array — accept all so the badge actually populates.
      const response = await apiGet<any>('/queue', { status: '' })
      const chats = Array.isArray(response) ? response : (response.queue || response.chats || [])
      const active = chats.filter(
        (c: any) => c.status === 'queued' || c.status === 'open'
      )
      setCount(active.length)
    } catch {
      // Silently fail — badge will just show stale count
    }
  }, [])

  const debouncedFetchCount = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchCount(), 300)
  }, [fetchCount])

  useEffect(() => {
    fetchCount()
  }, [fetchCount])

  useEffect(() => {
    if (!socket) return

    const onNewChat = () => {
      setCount((prev) => prev + 1)
      debouncedFetchCount()
    }
    const onChatAssigned = () => debouncedFetchCount()
    const onChatClosed = () => debouncedFetchCount()
    const onQueueUpdate = () => debouncedFetchCount()

    socket.on('new_chat', onNewChat)
    socket.on('chat_assigned', onChatAssigned)
    socket.on('chat_closed', onChatClosed)
    socket.on('queue_update', onQueueUpdate)

    return () => {
      socket.off('new_chat', onNewChat)
      socket.off('chat_assigned', onChatAssigned)
      socket.off('chat_closed', onChatClosed)
      socket.off('queue_update', onQueueUpdate)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [socket, debouncedFetchCount])

  return count
}
